import { useState, useCallback } from 'react'

interface PredictionFactors {
  xg_contribution: number
  xa_contribution: number
  clean_sheet_probability: number
  fixture_difficulty: number
  form_factor: number
  minutes_risk: number
  bonus_potential: number
}

interface NextFixture {
  fixture_id: number
  opponent: string
  home_away: 'home' | 'away'
  difficulty: number
  predicted_points: number
}

interface Prediction {
  player_id: number
  player_name: string
  team: string
  position: string
  predicted_points: number
  confidence: number
  factors: PredictionFactors
  next_fixtures: NextFixture[]
}

const API_BASE = 'https://fpl-predicted-points.siward.workers.dev'

export const usePredictions = () => {
  const [predictions, setPredictions] = useState<Prediction[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchPredictions = useCallback(async () => {
    setLoading(true)
    setError(null)
    
    try {
      const response = await fetch(`${API_BASE}/predictions/top?limit=50`)
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      const data = await response.json()
      setPredictions(data)
    } catch (err) {
      console.error('Failed to fetch predictions:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch predictions')
      
      // Fallback mock data for development
      setPredictions([
        {
          player_id: 1,
          player_name: "Haaland",
          team: "MCI",
          position: "FWD",
          predicted_points: 8.5,
          confidence: 0.85,
          factors: {
            xg_contribution: 2.1,
            xa_contribution: 0.3,
            clean_sheet_probability: 0,
            fixture_difficulty: 2,
            form_factor: 1.8,
            minutes_risk: 1,
            bonus_potential: 0.6
          },
          next_fixtures: [
            {
              fixture_id: 1,
              opponent: "ARS",
              home_away: "home",
              difficulty: 2,
              predicted_points: 8.5
            }
          ]
        },
        {
          player_id: 2,
          player_name: "Salah",
          team: "LIV",
          position: "MID",
          predicted_points: 7.8,
          confidence: 0.82,
          factors: {
            xg_contribution: 1.8,
            xa_contribution: 0.5,
            clean_sheet_probability: 0,
            fixture_difficulty: 3,
            form_factor: 1.6,
            minutes_risk: 1,
            bonus_potential: 0.5
          },
          next_fixtures: [
            {
              fixture_id: 2,
              opponent: "CHE",
              home_away: "away",
              difficulty: 3,
              predicted_points: 7.8
            }
          ]
        }
      ])
    } finally {
      setLoading(false)
    }
  }, [])

  return {
    predictions,
    loading,
    error,
    fetchPredictions
  }
}
